'use client'

import { useEffect, useRef } from 'react'
import { useRouter } from '@/i18n/routing'
import { auth } from '@/lib/firebase'
import { consumeRedirectResult, type GoogleAuthOutcome } from '@/lib/utils/googleAuth'
import { clearPostAuthIntent, markOnboardingIntent } from '@/lib/utils/postAuthIntent'

const DASHBOARD_PATH = '/dashboard'
const ONBOARDING_PATH = '/onboarding'

const routeForOutcome = (
  outcome: GoogleAuthOutcome,
  router: ReturnType<typeof useRouter>
) => {
  if (outcome.status !== 'success') return
  // Marca/limpia ANTES de navegar para que la login page no desvíe al
  // usuario nuevo por /dashboard si el efecto de auth llega después.
  if (outcome.isNewUser) {
    markOnboardingIntent(auth.currentUser?.uid)
  } else {
    clearPostAuthIntent()
  }
  const target = outcome.isNewUser ? ONBOARDING_PATH : DASHBOARD_PATH
  router.replace(target)
}

const GoogleAuthGate = () => {
  const router = useRouter()
  const routerRef = useRef(router)
  routerRef.current = router

  // Maneja el resultado de un redirect de Google (signInWithRedirect).
  // Se llama una sola vez al montar la app.
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      const outcome = await consumeRedirectResult()
      if (cancelled || !outcome) return
      routeForOutcome(outcome, routerRef.current)
    }
    run().catch((err) => console.error('consumeRedirectResult failed', err))
    return () => {
      cancelled = true
    }
  }, [])

  // Escucha los outcomes emitidos por loginWithGooglePopup / loginWithGoogleOneTap.
  //
  // NOTA: NO comprobamos el `user` del AuthContext aquí. Cuando
  // signInWithPopup / signInWithCredential resuelven, Firebase ya ha disparado
  // onAuthStateChanged internamente, pero el setState de React aún no se ha
  // propagado. Si gateáramos en `user`, leeríamos un valor stale (null) y
  // descartaríamos el evento, dejando al usuario en la landing. Como
  // `status === 'success'` solo se emite cuando Firebase confirma el sign-in,
  // la página de destino (/onboarding o /dashboard) verá el `user` correcto
  // cuando monte.
  useEffect(() => {
    const onOutcome = (event: Event) => {
      const customEvent = event as CustomEvent<GoogleAuthOutcome>
      routeForOutcome(customEvent.detail, routerRef.current)
    }
    window.addEventListener('google-auth-outcome', onOutcome)
    return () => window.removeEventListener('google-auth-outcome', onOutcome)
  }, [])

  return null
}

export default GoogleAuthGate
